<template>
  <div class="animated fadeIn">
    <div v-if="$route.name == 'QaList'" class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-header">
            <i class="fa fa-align-justify" /> Questions and Answers
            <router-link
              v-if="$_allowCreating"
              :to="'/admin/content/questions_and_answers/new'"
              class="pull-right btn btn-success btn-sm"
              exact
            >
              New
            </router-link>
            <button
              v-if="allowSearchAdministration"
              class="add-to-index-all btn btn-primary btn-sm pull-right"
              @click="$_addToIndexAll('questions-and-answers')"
            >
              Add all to search index
              <span class="btn-loader" style="display: none;" />
            </button>
            <button
              v-if="allowRegenerateThumbs"
              class="regenerate-thumbs btn btn-primary btn-sm pull-right"
              @click="$_regenerateThumbs('questions-and-answers', 'Questions and Answers')"
            >
              Regenerate thumbnails
              <span class="btn-loader" style="display: none;" />
            </button>
          </div>

          <div class="card-block">
            <input
              v-model="searchQuery"
              type="text"
              class="form-control"
              placeholder="Search by Q&A title"
            >
          </div>

          <EmptyBtns
            :empty-search="emptySearch"
            @showEmpty="() => emptySearch = true"
            @showAll="() => emptySearch = false"
          />

          <BulkBtns
            :class="{ hidden: !choosenEntities.length }"
            entity-type="questions_and_answers"
            unpublish-empty-endpoint="questions-and-answers/unpublish-empty"
            :choosen-entities="choosenEntities"
            :show-index-btn="true"
            @changePublishedBtns="$_changePublishedBtns"
            @clearChoosenEntities="() => choosenEntities = []"
          />

          <div class="card-block spinner-parent">
            <spinner ref="spinner" size="md" text="Loading ..." />

            <nav>
              <pager
                :total="total"
                :offset="offset"
                :size="perPage"
                @page-changed="$_changePage"
              />
            </nav>
            <div class="table-responsive">
              <table v-if="total" class="entities table table-bordered table-striped table-sm">
                <thead>
                  <tr>
                    <th class="table-list__cell--narrow">
                      <input ref="allEntities" type="checkbox" @change="$_selectAllEntities">
                    </th>
                    <th>ID</th>
                    <th>Title</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="item in entities" :key="item.id" :data-id="item.id">
                    <td class="table-list__cell--narrow text-center">
                      <input v-model="choosenEntities" type="checkbox" :value="item.id">
                    </td>
                    <td>{{ item.id }}</td>
                    <td>{{ item.title }}</td>
                    <td class="table-list__cell--narrow">
                      <button
                        v-if="allowSearchAdministration"
                        class="add-to-index-single btn btn-primary btn-sm"
                        @click="$_addToIndexSingle('questions-and-answers', item.id)"
                      >
                        Add to index
                        <span class="btn-loader" style="display: none;" />
                      </button>
                      <router-link
                        v-if="$_allowUpdating(item)"
                        class="btn btn-primary btn-sm"
                        :to="{ name: 'QaEdit', params: { id: item.id }}"
                      >
                        Edit
                      </router-link>
                      <button
                        v-if="allowPublishing"
                        class="btn btn-primary btn-sm"
                        @click="$_changePublished(item.published, 'questions_and_answers', item.id)"
                      >
                        <template v-if="item.published">Unpublish</template>
                        <template v-else>Publish</template>
                        <span :ref="`publishBtnLoader_${item.id}`" class="btn-loader" style="display: none;" />
                      </button>
                      <button
                        class="btn btn-primary btn-sm"
                        @click="$_regThumb(item, 'questions_and_answers', 'QA')"
                      >
                        Reg. thumb
                        <span :ref="`_regThumbBtnLoader_${item.id}`" class="btn-loader" style="display: none;" />
                      </button>
                      <button
                        v-if="$_allowDeleting(item)"
                        class="btn btn-danger btn-sm"
                        @click="$_deleteEntity(item, 'questions-and-answers/', 'QA')"
                      >
                        Delete
                        <span :ref="`deleteBtnLoader_${item.id}`" class="btn-loader" style="display: none;" />
                      </button>
                      <open-on-site
                        :source-route="item.route"
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div v-if="total === 0">
              There are no entities for your request
            </div>

            <nav>
              <pager
                :total="total"
                :offset="offset"
                :size="perPage"
                @page-changed="$_changePage"
              />
            </nav>
          </div>
        </div>
      </div><!--/.col-->
    </div><!--/.row-->
    <div class="row">
      <div class="col-12">
        <router-view />
      </div>
    </div>
  </div>
</template>

<script>
import _ from 'lodash';
import Pager from '@/global/components/Pager';
import BulkBtns from '@/global/components/BulkBtns';
import EmptyBtns from '@/global/components/EmptyBtns';
import OpenOnSiteButton from '@/global/components/OpenOnSiteButton';
// Mixins
import ListPageMixin from '@/mixins/ListPageMixin';
import CommonPageMixin from '@/mixins/CommonPageMixin';

export default {
  name: 'QaList',

  components: {
    pager: Pager,
    BulkBtns,
    EmptyBtns,
    'open-on-site': OpenOnSiteButton,
  },

  mixins: [ListPageMixin, CommonPageMixin],

  methods: {
    getSource() {
      if (this.$refs.spinner) { this.$refs.spinner.show(); }
      const searchString = encodeURI(this.searchQuery);
      const page = this.perPage ? (this.offset / this.perPage + 1) : 1;
      const searchProcessId = Math.random();
      this.searchProcessId = searchProcessId;
      this.$apiHttp.get(`${this.$config.app_api.prefix}questions-and-answers${this.emptySearch ? '/show-empty' : ''}`, {
        params: {
          search_string: searchString,
          page,
        },
      }).then((response) => {
        if (this.searchProcessId === searchProcessId) {
          if (this.$refs.spinner) { this.$refs.spinner.hide(); }
          this.total = response.data.meta.pagination.total;
          this.perPage = response.data.meta.pagination.per_page;

          const qaArr = _.cloneDeep(response.data);
          delete qaArr.meta;
          this.entities = Object.keys(qaArr).map(e => qaArr[e]);
        }
      }).catch((error) => { this.$_errorCallback(error); });
    },
  },
};
</script>



// WEBPACK FOOTER //
// Index.vue?67595e66